import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  styled
} from '@mui/material';
import {
  CalendarToday,
  Speed,
  ShowChart,
  Videocam,
  Twitter,
  Facebook,
  LinkedIn,
  Instagram
} from '@mui/icons-material';

// Custom styled components
const GradientButton = styled(Button)({
  background: 'linear-gradient(45deg, #1A2027 30%, #121416 90%)',
  color: 'white',
  padding: '12px 24px',
  '&:hover': {
    background: 'linear-gradient(45deg, #121416 30%, #0A0C0E 90%)',
    transform: 'scale(1.05)'
  },
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
});

const OutlineButton = styled(Button)({
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  border: '1px solid #e0e0e0',
  color: '#1A2027',
  padding: '12px 24px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)'
  },
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
});

const FeatureCard = styled(Card)({
  backdropFilter: 'blur(10px)',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  border: '1px solid #e0e0e0',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)'
  }
});

const GradientIconBox = styled(Box)(({ gradient }) => ({
  background: gradient,
  padding: '16px',
  borderRadius: '8px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

function App() {
  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      color: '#1A2027'
    }}>
      {/* Hero Section */}
      <Box sx={{
        position: 'relative',
        minHeight: '500px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20minimalist%20parking%20garage%20interior%20with%20clean%20white%20walls%20and%20subtle%20geometric%20patterns%2C%20bright%20natural%20lighting%2C%20contemporary%20architecture%20with%20glass%20elements%2C%20professional%20and%20elegant%20atmosphere%20with%20light%20gray%20accents&width=1440&height=800&seq=1&orientation=landscape)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.95)'
        }} />
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)',
          zIndex: 10
        }} />
        <Container sx={{ position: 'relative', zIndex: 20 }}>
          <Box sx={{ maxWidth: '600px' }}>
            <Typography variant="h2" component="h1" sx={{
              fontWeight: 'bold',
              mb: 4,
              color: '#1A2027',
              lineHeight: 1.2,
              fontSize: { xs: '2.5rem', md: '3.75rem' }
            }}>
              Welcome to Dock Parking
            </Typography>
            <Paper elevation={3} sx={{
              backdropFilter: 'blur(6px)',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              p: 4,
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
            }}>
              <Typography variant="body1" sx={{
                fontSize: { xs: '1rem', md: '1.25rem' },
                lineHeight: 1.6,
                color: '#374151'
              }}>
                Dock Parking Management Software offers a smart, centralized solution
                to streamline operations across modern parking facilities. Book slots
                via our integrated chatbot, and manage all operations — from
                reservations to real-time reporting — through a single dashboard.
              </Typography>
            </Paper>
            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <GradientButton>
                Get Started
              </GradientButton>
              <OutlineButton>
                Learn More
              </OutlineButton>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Feature Cards */}
      <Container sx={{ py: 10 }}>
        <Typography variant="h3" component="h2" sx={{
          fontWeight: 'bold',
          mb: 8,
          textAlign: 'center',
          color: '#1A2027'
        }}>
          Our Key Features
        </Typography>
        <Grid container spacing={4}>
          {/* Card 1 */}
          <Grid item xs={12} md={6}>
            <FeatureCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <GradientIconBox gradient="linear-gradient(135deg, #1A2027 0%, #121416 100%)">
                    <CalendarToday fontSize="large" />
                  </GradientIconBox>
                  <Box sx={{ ml: 3 }}>
                    <Typography variant="h5" component="h3" sx={{
                      fontWeight: 'bold',
                      mb: 2,
                      '&:hover': { color: 'primary.light' }
                    }}>
                      Smart Booking
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Seamless slot booking through AI-powered chatbot. Reserve parking spaces effortlessly with voice commands or text interactions.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </FeatureCard>
          </Grid>

          {/* Card 2 */}
          <Grid item xs={12} md={6}>
            <FeatureCard sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <GradientIconBox gradient="linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)">
                    <Speed fontSize="large" />
                  </GradientIconBox>
                  <Box sx={{ ml: 3 }}>
                    <Typography variant="h5" component="h3" sx={{
                      fontWeight: 'bold',
                      mb: 2,
                      '&:hover': { color: 'secondary.light' }
                    }}>
                      Real-time Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Comprehensive dashboard for instant operational overview. Monitor occupancy, manage resources, and optimize space utilization in real-time.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </FeatureCard>
          </Grid>

          {/* Card 3 */}
          <Grid item xs={12} md={6}>
            <FeatureCard sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <GradientIconBox gradient="linear-gradient(135deg, #00acc1 0%, #1976d2 100%)">
                    <ShowChart fontSize="large" />
                  </GradientIconBox>
                  <Box sx={{ ml: 3 }}>
                    <Typography variant="h5" component="h3" sx={{
                      fontWeight: 'bold',
                      mb: 2,
                      '&:hover': { color: 'info.light' }
                    }}>
                      Automated Reporting
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Generate detailed reports and analytics automatically. Gain insights into usage patterns, revenue streams, and operational efficiency.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </FeatureCard>
          </Grid>

          {/* Card 4 */}
          <Grid item xs={12} md={6}>
            <FeatureCard sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <GradientIconBox gradient="linear-gradient(135deg, #00c853 0%, #2e7d32 100%)">
                    <Videocam fontSize="large" />
                  </GradientIconBox>
                  <Box sx={{ ml: 3 }}>
                    <Typography variant="h5" component="h3" sx={{
                      fontWeight: 'bold',
                      mb: 2,
                      '&:hover': { color: 'success.light' }
                    }}>
                      Facility Monitoring
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Live monitoring and status updates of parking facilities. Track security, maintenance needs, and environmental conditions in real-time.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </FeatureCard>
          </Grid>
        </Grid>
      </Container>

      {/* Call to Action */}
      <Container sx={{ py: 8 }}>
        <Paper elevation={6} sx={{
          backdropFilter: 'blur(16px)',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          p: 8,
          borderRadius: 3,
          border: '1px solid #e0e0e0',
          boxShadow: '0 16px 64px rgba(0, 0, 0, 0.2)'
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ mb: { xs: 4, md: 0 }, mr: { md: 4 } }}>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
                Ready to Transform Your Parking Management?
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Join hundreds of facilities already using Dock Parking to streamline their operations.
              </Typography>
            </Box>
            <GradientButton sx={{ fontSize: '1.125rem' }}>
              Schedule a Demo
            </GradientButton>
          </Box>
        </Paper>
      </Container>

      {/* Footer */}
      {/* <Box component="footer" sx={{
        backgroundColor: '#f5f5f5',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid #e0e0e0',
        py: 6
      }}>
        <Container>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between'
          }}>
            <Box sx={{ mb: { xs: 6, md: 0 } }}>
              <Typography variant="h4" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
                Dock Parking
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '400px' }}>
                Smart solutions for modern parking facilities. Streamline operations and enhance user experience.
              </Typography>
            </Box>
            <Grid container spacing={4} sx={{ width: { xs: '100%', md: 'auto' } }}>
              <Grid item xs={6} md={4}>
                <Typography variant="h6" component="h4" sx={{ fontWeight: 'semibold', mb: 2 }}>
                  Product
                </Typography>
                <List dense>
                  <ListItem disablePadding>
                    <ListItemText primary="Features" sx={{ '& .MuiListItemText-primary': { color: 'text.secondary', '&:hover': { color: 'text.primary' }, cursor: 'pointer' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText primary="Pricing" sx={{ '& .MuiListItemText-primary': { color: 'text.secondary', '&:hover': { color: 'text.primary' }, cursor: 'pointer' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText primary="Integrations" sx={{ '& .MuiListItemText-primary': { color: 'text.secondary', '&:hover': { color: 'text.primary' }, cursor: 'pointer' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText primary="Updates" sx={{ '& .MuiListItemText-primary': { color: 'text.secondary', '&:hover': { color: 'text.primary' }, cursor: 'pointer' } }} />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="h6" component="h4" sx={{ fontWeight: 'semibold', mb: 2 }}>
                  Company
                </Typography>
                <List dense>
                  <ListItem disablePadding>
                    <ListItemText primary="About" sx={{ '& .MuiListItemText-primary': { color: 'text.secondary', '&:hover': { color: 'text.primary' }, cursor: 'pointer' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText primary="Careers" sx={{ '& .MuiListItemText-primary': { color: 'text.secondary', '&:hover': { color: 'text.primary' }, cursor: 'pointer' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText primary="Contact" sx={{ '& .MuiListItemText-primary': { color: 'text.secondary', '&:hover': { color: 'text.primary' }, cursor: 'pointer' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText primary="Partners" sx={{ '& .MuiListItemText-primary': { color: 'text.secondary', '&:hover': { color: 'text.primary' }, cursor: 'pointer' } }} />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="h6" component="h4" sx={{ fontWeight: 'semibold', mb: 2 }}>
                  Resources
                </Typography>
                <List dense>
                  <ListItem disablePadding>
                    <ListItemText primary="Documentation" sx={{ '& .MuiListItemText-primary': { color: 'text.secondary', '&:hover': { color: 'text.primary' }, cursor: 'pointer' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText primary="Blog" sx={{ '& .MuiListItemText-primary': { color: 'text.secondary', '&:hover': { color: 'text.primary' }, cursor: 'pointer' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText primary="Support" sx={{ '& .MuiListItemText-primary': { color: 'text.secondary', '&:hover': { color: 'text.primary' }, cursor: 'pointer' } }} />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText primary="API" sx={{ '& .MuiListItemText-primary': { color: 'text.secondary', '&:hover': { color: 'text.primary' }, cursor: 'pointer' } }} />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </Box>
          <Divider sx={{ my: 4 }} />
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 2, md: 0 } }}>
              © 2025 Dock Parking. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <IconButton aria-label="Twitter" sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
                <Twitter />
              </IconButton>
              <IconButton aria-label="Facebook" sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
                <Facebook />
              </IconButton>
              <IconButton aria-label="LinkedIn" sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
                <LinkedIn />
              </IconButton>
              <IconButton aria-label="Instagram" sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
                <Instagram />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box> */}
    </Box>
  );
}

export default App;